import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { fetchLabBookingData } from "../../API_Service/LabBooking_Slice";
import { companyID } from "../../Common/UIComponents";
import { useModalBackClose } from "../../Common/useModalHistory";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseLocalISO(isoString) {
  if (!isoString) return null;
  const [datePart, timePart = "00:00:00"] = isoString.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const parts = timePart.split(":");
  const h = parseInt(parts[0], 10) || 0;
  const mi = parseInt(parts[1], 10) || 0;
  return new Date(y, mo - 1, d, h, mi, 0, 0);
}

function toLocalISOString(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
  );
}

function toDateInputValue(date) {
  if (!date || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toTimeInputValue(date) {
  if (!date || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDisplay(date) {
  if (!date || isNaN(date.getTime())) return "—";
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const h = date.getHours();
  const min = String(date.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${DAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()} · ${String(h12).padStart(2, "0")}:${min} ${ampm}`;
}

function getDurationLabel(startDate, endDate, bookableDays, totalHours) {
  if (!startDate || !endDate) return "—";

  // If bookableDays provided, use count + planned hours
  if (bookableDays && bookableDays.length > 0) {
    const days = bookableDays.length;
    const dayLabel = days === 1 ? "1 day" : `${days} days`;
    if (totalHours) return `${totalHours}h · ${days}d (${dayLabel})`;
    return `${days}d (${dayLabel})`;
  }

  const diffMs = endDate - startDate;
  if (diffMs <= 0) return "Invalid range";
  const totalMins = Math.round(diffMs / 60000);
  const days = Math.floor(totalMins / 1440);
  const hrs = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  return parts.join(" ") || "0m";
}

const TEAL = "#08757c";
const TEAL_DARK = "#005b60";

// ─── Custom Select — portal-based so it escapes modal overflow:hidden ─────────
function CustomSelect({ value, onChange, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [panelStyle, setPanelStyle] = useState({});
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 30);
    else setSearch("");
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPanelStyle({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen((v) => !v);
  };

  const filtered = options.filter((o) =>
    (o.label ?? "").toLowerCase().includes(search.toLowerCase()),
  );
  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <>
      <div ref={triggerRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={handleToggle}
          title={selected?.label || ""}
          style={{
            width: "100%",
            height: 44,
            padding: "0 36px 0 16px",
            background: disabled ? "#f3f4f6" : "#e5e9e9",
            border: open ? `2px solid ${TEAL}` : "1px solid #e5e9e9",
            borderRadius: 12,
            fontSize: 13,
            color: selected ? "#181c1d" : "rgba(62,73,74,0.5)",
            outline: "none",
            fontFamily: "inherit",
            textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            boxSizing: "border-box",
            transition: "border 0.15s",
          }}
        >
          {selected ? selected.label : placeholder}
        </button>
        <span
          className="material-symbols-outlined"
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            fontSize: 18,
            color: "#3e494a",
            pointerEvents: "none",
            transition: "transform 0.2s",
          }}
        >
          expand_more
        </span>
      </div>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              ...panelStyle,
              background: "#fff",
              border: "1px solid #e5e9e9",
              borderRadius: 12,
              boxShadow: "0 8px 28px rgba(0,0,0,0.15)",
              zIndex: 9999,
              overflow: "hidden",
            }}
          >
            {/* Search */}
            <div
              style={{
                padding: "10px 12px",
                borderBottom: "1px solid #f1f4f4",
              }}
            >
              <div style={{ position: "relative" }}>
                <span
                  className="material-symbols-outlined"
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 16,
                    color: "rgba(62,73,74,0.5)",
                    pointerEvents: "none",
                  }}
                >
                  search
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  style={{
                    width: "100%",
                    padding: "7px 28px",
                    fontSize: 12,
                    background: "#f1f4f4",
                    border: "none",
                    borderRadius: 8,
                    outline: "none",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    color: "#181c1d",
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: 14, color: "rgba(62,73,74,0.5)" }}
                    >
                      close
                    </span>
                  </button>
                )}
              </div>
            </div>
            {/* Options */}
            <ul
              style={{
                maxHeight: 200,
                overflowY: "auto",
                margin: 0,
                padding: "4px 0",
                listStyle: "none",
              }}
            >
              {filtered.length === 0 ? (
                <li
                  style={{
                    padding: "10px 16px",
                    fontSize: 12,
                    color: "rgba(62,73,74,0.5)",
                    textAlign: "center",
                  }}
                >
                  No results found
                </li>
              ) : (
                filtered.map((o) => {
                  const isActive = String(o.value) === String(value);
                  return (
                    <li
                      key={o.value}
                      onClick={() => {
                        onChange(String(o.value));
                        setOpen(false);
                        setSearch("");
                      }}
                      title={o.label ?? ""}
                      style={{
                        padding: "10px 16px",
                        fontSize: 13,
                        cursor: "pointer",
                        listStyle: "none",
                        color: isActive ? TEAL : "#181c1d",
                        fontWeight: isActive ? 600 : 400,
                        background: isActive ? `${TEAL}10` : "transparent",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = "#f1f4f4";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = isActive
                            ? `${TEAL}10`
                            : "transparent";
                      }}
                    >
                      {o.label ?? ""}
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    backdropFilter: "blur(4px)",
  },
  modal: {
    position: "relative",
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    width: "100%",
    maxWidth: 480,
    margin: "0 16px",
    overflow: "hidden",
    fontFamily: "'DM Sans','Segoe UI',sans-serif",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: `linear-gradient(135deg,${TEAL},${TEAL_DARK})`,
    padding: "20px 24px",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: 0,
  },
  headerSub: { fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 4 },
  scrollBody: { overflowY: "auto", flex: 1 },
  section: { padding: "16px 24px", borderBottom: "1px solid #e5e9e9" },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "rgba(62,73,74,0.55)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 10,
  },
  timeRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  timeCard: (active) => ({
    background: active ? `${TEAL}10` : "#f7fafa",
    border: `1.5px solid ${active ? TEAL : "#e5e9e9"}`,
    borderRadius: 12,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "all 0.15s",
  }),
  timeCardLabel: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "rgba(62,73,74,0.5)",
    marginBottom: 4,
  },
  timeCardValue: {
    fontSize: 12,
    fontWeight: 600,
    color: "#181c1d",
    lineHeight: 1.3,
  },
  editBox: {
    marginTop: 10,
    background: "#f0fdfa",
    border: `1px solid ${TEAL}33`,
    borderLeft: `3px solid ${TEAL}`,
    borderRadius: 10,
    padding: "12px 14px",
  },
  inputRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginBottom: 8,
  },
  inputLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "rgba(62,73,74,0.6)",
    marginBottom: 4,
    display: "block",
  },
  input: {
    width: "100%",
    height: 34,
    padding: "0 8px",
    border: `1px solid #e5e9e9`,
    borderRadius: 8,
    fontSize: 12,
    color: "#181c1d",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  durationBadge: (valid) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: valid ? `${TEAL}15` : "#fee2e2",
    color: valid ? TEAL : "#dc2626",
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 20,
  }),
  select: {
    width: "100%",
    height: 44,
    padding: "0 16px",
    background: "#f7fafa",
    border: "1px solid #e5e9e9",
    borderRadius: 12,
    fontSize: 13,
    color: "#181c1d",
    outline: "none",
    appearance: "none",
    fontFamily: "inherit",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%233e494a' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 14px center",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    padding: "14px 24px",
    background: "#f7fafa",
    borderTop: "1px solid #e5e9e9",
    flexShrink: 0,
  },
  cancelBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    color: "#3e494a",
    background: "none",
    border: "none",
    cursor: "pointer",
    borderRadius: 8,
  },
  confirmBtn: (disabled) => ({
    padding: "9px 22px",
    background: disabled
      ? "#e5e7eb"
      : `linear-gradient(135deg,${TEAL},${TEAL_DARK})`,
    color: disabled ? "#9ca3af" : "#fff",
    fontSize: 13,
    fontWeight: 700,
    border: "none",
    borderRadius: 12,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: disabled ? "none" : `0 4px 14px ${TEAL}40`,
    transition: "all 0.15s",
  }),
};

// ─── Main Component ────────────────────────────────────────────────────────────
const BookingConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  bookingSummary,
  bookableDays,
}) => {
  const dispatch = useDispatch();

  const [projects, setProjects] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedTestType, setSelectedTestType] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTests, setLoadingTests] = useState(false);
  const [remarks, setRemarks] = useState("");
  useModalBackClose(isOpen, onClose);

  // ── Use refs for dates so handleConfirm always reads latest value ─────────────
  // (avoids stale-closure bug where setState hasn't flushed yet)
  const [startDate, _setStartDate] = useState(null);
  const [endDate, _setEndDate] = useState(null);
  const startRef = useRef(null);
  const endRef = useRef(null);

  const setStartDate = (valOrFn) => {
    _setStartDate((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      startRef.current = next;
      return next;
    });
  };

  const setEndDate = (valOrFn) => {
    _setEndDate((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      endRef.current = next;
      return next;
    });
  };

  const [editingField, setEditingField] = useState(null);

  // ── Seed from bookingSummary on open ─────────────────────────────────────────
  useEffect(() => {
    if (isOpen && bookingSummary) {
      const s = parseLocalISO(bookingSummary.start);
      const e = parseLocalISO(bookingSummary.end);
      // Log to verify seed values are correct
      console.log(
        "[Modal seed] start:",
        toLocalISOString(s),
        "end:",
        toLocalISOString(e),
      );
      setStartDate(s);
      setEndDate(e);
      setEditingField(null);
      setRemarks("");
    }
  }, [isOpen, bookingSummary]);

  // ── Fetch projects ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setLoadingProjects(true);
    dispatch(fetchLabBookingData({ companyID }))
      .then((res) => {
        const data =
          res?.payload?.data?.data || res?.payload?.data || res?.data;
        if (data?.projects)
          setProjects(Array.isArray(data.projects) ? data.projects : []);
      })
      .catch(console.error)
      .finally(() => setLoadingProjects(false));
  }, [isOpen, dispatch]);

  // ── Fetch tests ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedProject) {
      setTestTypes([]);
      setSelectedTestType("");
      return;
    }
    setLoadingTests(true);
    dispatch(fetchLabBookingData({ companyID, projectId: selectedProject }))
      .then((res) => {
        const data =
          res?.payload?.data?.data || res?.payload?.data || res?.data;
        const valid = Array.isArray(data?.tests)
          ? data.tests.filter((t) => t.Test_Name && t.Test_Name.trim() !== "")
          : [];
        setTestTypes(valid);
      })
      .catch(console.error)
      .finally(() => setLoadingTests(false));
    setSelectedTestType("");
  }, [selectedProject, dispatch]);

  // ── Date change — preserves the time, only swaps Y/M/D ───────────────────────
  const handleDateChange = (field, dateValue) => {
    if (!dateValue) return;
    const [y, m, d] = dateValue.split("-").map(Number);
    if (field === "start") {
      setStartDate((prev) => {
        const base = prev || new Date();
        const next = new Date(
          y,
          m - 1,
          d,
          base.getHours(),
          base.getMinutes(),
          0,
          0,
        );
        console.log("[date change] start →", toLocalISOString(next));
        return next;
      });
    } else {
      setEndDate((prev) => {
        const base = prev || new Date();
        const next = new Date(
          y,
          m - 1,
          d,
          base.getHours(),
          base.getMinutes(),
          0,
          0,
        );
        console.log("[date change] end →", toLocalISOString(next));
        return next;
      });
    }
  };

  // ── Time change — preserves the Y/M/D, only swaps H/M ────────────────────────
  const handleTimeChange = (field, timeValue) => {
    if (!timeValue) return;
    const parts = timeValue.split(":");
    const h = parseInt(parts[0], 10);
    const mi = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(mi)) return;

    if (field === "start") {
      setStartDate((prev) => {
        const base = prev || new Date();
        const next = new Date(
          base.getFullYear(),
          base.getMonth(),
          base.getDate(),
          h,
          mi,
          0,
          0,
        );
        console.log("[time change] start →", toLocalISOString(next));
        return next;
      });
    } else {
      setEndDate((prev) => {
        const base = prev || new Date();
        const next = new Date(
          base.getFullYear(),
          base.getMonth(),
          base.getDate(),
          h,
          mi,
          0,
          0,
        );
        console.log("[time change] end →", toLocalISOString(next));
        return next;
      });
    }
  };

  const selectedTestData = testTypes.find(
    (t) => t.Project_Test_Det_Id === parseInt(selectedTestType),
  );
  const selectedTestHours = selectedTestData?.Total_Hours ?? "";

  const isValidRange =
    startDate &&
    endDate &&
    !isNaN(startDate.getTime()) &&
    !isNaN(endDate.getTime()) &&
    endDate > startDate;

  // ── Confirm — reads from refs to avoid stale closure ─────────────────────────
  const handleConfirm = () => {
    // Read from refs so we always get the latest value regardless of render timing
    const finalStart = startRef.current;
    const finalEnd = endRef.current;

    console.log("[Confirm] finalStart:", toLocalISOString(finalStart));
    console.log("[Confirm] finalEnd:", toLocalISOString(finalEnd));

    if (!finalStart || !finalEnd || finalEnd <= finalStart) return;

    const projectData = projects.find(
      (p) => p.Project_Id === parseInt(selectedProject),
    );
    const testTypeData = testTypes.find(
      (t) => t.Project_Test_Det_Id === parseInt(selectedTestType),
    );

    const startStr = toLocalISOString(finalStart);
    const endStr = toLocalISOString(finalEnd);

    const durationHours = bookingSummary?.totalHours
      ? bookingSummary.totalHours
      : Math.round(((finalEnd - finalStart) / 3600000) * 10) / 10;

    console.log("[Confirm] sending to API → start:", startStr, "end:", endStr);

    onConfirm({
      projectId: selectedProject || "",
      testTypeId: selectedTestType || "",
      projectName: projectData?.Project_Name || "",
      testTypeName: testTypeData?.Test_Name || "",
      startDateTimeStr: startStr,
      endDateTimeStr: endStr,
      startDateTime: finalStart,
      endDateTime: finalEnd,
      durationHours,
      remarks: remarks,
    });
  };

  const handleClose = () => {
    setSelectedProject("");
    setSelectedTestType("");
    setTestTypes([]);
    setEditingField(null);
    setRemarks("");
    onClose();
  };

  if (!isOpen) return null;

  const duration = bookingSummary?.bookableDays
    ? getDurationLabel(
        startDate,
        endDate,
        bookingSummary.bookableDays,
        bookingSummary.totalHours,
      )
    : getDurationLabel(startDate, endDate);

  return (
    <div style={S.overlay}>
      <div style={S.backdrop} onClick={handleClose} />
      <div style={S.modal}>
        {/* Header */}
        <div style={S.header}>
          <h3 style={S.headerTitle}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 20 }}
            >
              confirmation_number
            </span>
            Confirm Booking Details
          </h3>
          <p style={S.headerSub}>
            Review your schedule and select project details (optional)
          </p>
        </div>

        {/* Scrollable body */}
        <div style={S.scrollBody}>
          {/* Overlap Warning */}
          {bookingSummary?.hasOverlap && (
            <div style={{
              background: "#fffbeb",
              borderBottom: "1px solid #fef3c7",
              padding: "12px 24px",
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
            }}>
              <span className="material-symbols-outlined" style={{ color: "#d97706", fontSize: 20 }}>warning</span>
              <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.4 }}>
                <strong style={{ display: "block", marginBottom: 2 }}>Already Booked</strong>
                This lab is already booked for this time. You can still proceed with booking if needed.
              </div>
            </div>
          )}

          {/* Lab + Duration */}
          <div
            style={{
              ...S.section,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <div>
              <div style={S.sectionLabel}>Lab</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#181c1d" }}>
                {bookingSummary?.lab || "—"}
              </div>
            </div>
            <div>
              <div style={S.sectionLabel}>Duration</div>
              <div style={S.durationBadge(isValidRange)}>{duration}</div>
            </div>
          </div>

          {/* Schedule */}
          <div style={S.section}>
            <div style={S.sectionLabel}>Schedule</div>

            <div style={S.timeRow}>
              {/* Start card */}
              <div
                style={S.timeCard(editingField === "start")}
                onClick={() =>
                  setEditingField(editingField === "start" ? null : "start")
                }
              >
                <div style={S.timeCardLabel}>Start</div>
                <div style={S.timeCardValue}>{formatDisplay(startDate)}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: TEAL,
                    marginTop: 4,
                    fontWeight: 600,
                  }}
                >
                  {editingField === "start" ? "▲ close" : "✏ edit"}
                </div>
              </div>

              {/* End card */}
              <div
                style={S.timeCard(editingField === "end")}
                onClick={() =>
                  setEditingField(editingField === "end" ? null : "end")
                }
              >
                <div style={S.timeCardLabel}>End</div>
                <div style={S.timeCardValue}>{formatDisplay(endDate)}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: TEAL,
                    marginTop: 4,
                    fontWeight: 600,
                  }}
                >
                  {editingField === "end" ? "▲ close" : "✏ edit"}
                </div>
              </div>
            </div>

            {/* Edit panel */}
            {editingField && (
              <div style={S.editBox}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: TEAL,
                    marginBottom: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Edit {editingField === "start" ? "Start" : "End"} Time
                </div>
                <div style={S.inputRow}>
                  <div>
                    <label style={S.inputLabel}>Date</label>
                    <input
                      type="date"
                      style={S.input}
                      value={toDateInputValue(
                        editingField === "start" ? startDate : endDate,
                      )}
                      onChange={(e) =>
                        handleDateChange(editingField, e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label style={S.inputLabel}>Time</label>
                    <input
                      type="time"
                      style={S.input}
                      value={toTimeInputValue(
                        editingField === "start" ? startDate : endDate,
                      )}
                      onChange={(e) =>
                        handleTimeChange(editingField, e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Live preview of the edited value */}
                <div style={{ fontSize: 11, color: "#3e494a", marginTop: 4 }}>
                  Preview:{" "}
                  <strong>
                    {formatDisplay(
                      editingField === "start" ? startDate : endDate,
                    )}
                  </strong>
                </div>

                {!isValidRange && startDate && endDate && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#dc2626",
                      fontWeight: 500,
                      marginTop: 6,
                    }}
                  >
                    ⚠ End time must be after start time
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Project + Test + Remarks */}
          <div
            style={{
              padding: "16px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {/* Project */}
            <div>
              <label
                style={{ ...S.sectionLabel, display: "block", marginBottom: 8 }}
              >
                Project{" "}
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: 10,
                    fontWeight: 400,
                    textTransform: "none",
                  }}
                >
                  (Optional)
                </span>
              </label>
              <CustomSelect
                value={selectedProject}
                onChange={setSelectedProject}
                options={projects.map((p) => ({
                  value: p.Project_Id,
                  label: p.Project_Name,
                }))}
                placeholder={
                  loadingProjects
                    ? "Loading projects…"
                    : "Select a project (optional)"
                }
                disabled={loadingProjects}
              />
            </div>

            {/* Test Type */}
            <div>
              <label
                style={{ ...S.sectionLabel, display: "block", marginBottom: 8 }}
              >
                Test Name{" "}
                <span
                  style={{
                    color: "#9ca3af",
                    fontSize: 10,
                    fontWeight: 400,
                    textTransform: "none",
                  }}
                >
                  (Optional)
                </span>
              </label>
              <CustomSelect
                value={selectedTestType}
                onChange={setSelectedTestType}
                options={testTypes.map((t) => ({
                  value: t.Project_Test_Det_Id,
                  label: t.Test_Name,
                }))}
                placeholder={
                  loadingTests
                    ? "Loading test types…"
                    : !selectedProject
                      ? "Select a project first"
                      : testTypes.length === 0
                        ? "No test types available"
                        : "Select a test type (optional)"
                }
                disabled={!selectedProject || loadingTests}
              />
            </div>
            {selectedTestType && (
              <div>
                <label
                  style={{
                    ...S.sectionLabel,
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  Total Hours
                </label>
                <input
                  type="text"
                  value={
                    selectedTestHours !== "" ? `${selectedTestHours} hrs` : "—"
                  }
                  readOnly
                  style={{
                    ...S.input,
                    height: 44,
                    background: "#f3f4f6",
                    cursor: "not-allowed",
                    color: "#3e494a",
                    fontWeight: 600,
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {/* end scrollBody */}

        {/* Footer */}
        <div style={S.footer}>
          <button style={S.cancelBtn} onClick={handleClose}>
            Cancel
          </button>
          <button
            style={S.confirmBtn(!isValidRange)}
            onClick={handleConfirm}
            disabled={!isValidRange}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16 }}
            >
              check
            </span>
            Confirm Booking
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationModal;
